import { apiFetch } from "api/auth";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

interface ApiErrorResponse {
  success: false;
  message?: string;
  error?: string;
}

async function parseResponse<T>(res: Response): Promise<T> {
  const json = (await res.json()) as
    | ApiSuccessResponse<T>
    | ApiErrorResponse;
  if (!res.ok) {
    const msg =
      (json as ApiErrorResponse).message ||
      (json as ApiErrorResponse).error ||
      `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return (json as ApiSuccessResponse<T>).data;
}

// ---------------------------------------------------------------------------
// List leads
// ---------------------------------------------------------------------------

export interface OppositionListItem {
  id: string;
  leadNo: string;
  clientName: string;
  email: string | null;
  phone: string | null;
  status: string;
  stage: string | null;
  markName: string | null;
  createdAt: string;
}

export interface OppositionListResult {
  items: OppositionListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type OppositionStageFilter = "DISCUSSION" | "FILING" | "STATUS";
export type OppositionSort = "newest" | "oldest";

export interface ListOppositionLeadsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  stage?: OppositionStageFilter;
  sort?: OppositionSort;
}

export async function listOppositionLeads(
  params: ListOppositionLeadsParams = {}
): Promise<OppositionListResult> {
  const q = new URLSearchParams();
  if (params.page != null) q.set("page", String(params.page));
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.search) q.set("search", params.search);
  if (params.status) q.set("status", params.status);
  if (params.stage) q.set("stage", params.stage);
  if (params.sort) q.set("sort", params.sort);

  const res = await apiFetch(`${API_BASE}/opposition/leads?${q.toString()}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  return parseResponse<OppositionListResult>(res);
}

// ---------------------------------------------------------------------------
// Next lead number
// ---------------------------------------------------------------------------

export async function getOppositionNextLeadNumber(): Promise<{ leadNo: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/next-lead-number`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ leadNo: string }>(res);
}

// ---------------------------------------------------------------------------
// Get discussion details (edit / prefill mode)
// ---------------------------------------------------------------------------

export interface OppositionDiscussionDetails {
  leadId: string;
  leadNo: string;
  module: string;
  stage: string | null;
  initiationType: string | null;
  referenceLeadId: string | null;
  referenceLead: { id: string; leadNo: string; module: string } | null;
  preliminary: {
    markName: string | null;
    contactName: string;
    phone: string | null;
    email: string | null;
    callTime: string | null;
  };
}

export async function getOppositionDiscussionDetails(
  leadId: string
): Promise<OppositionDiscussionDetails> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionDiscussionDetails>(res);
}

// ---------------------------------------------------------------------------
// Create preliminary discussion
// ---------------------------------------------------------------------------

export type OppositionInitiationType = "CLIENT_INITIATED" | "EXISTING_LEAD";

export interface CreateOppositionDiscussionPayload {
  markName: string;
  contactName: string;
  phone: string;
  email?: string;
  callTime: string;
  initiationType: OppositionInitiationType;
  referenceLeadId?: string;
}

export interface CreateOppositionDiscussionResponse {
  leadId: string;
  leadNo: string;
  initiationType: OppositionInitiationType;
  referenceLeadId: string | null;
}

export async function createOppositionDiscussion(
  payload: CreateOppositionDiscussionPayload
): Promise<CreateOppositionDiscussionResponse> {
  const body: Record<string, unknown> = { ...payload };
  if (payload.initiationType === "CLIENT_INITIATED") {
    delete body.referenceLeadId;
  }
  const res = await apiFetch(`${API_BASE}/opposition/discussion`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseResponse<CreateOppositionDiscussionResponse>(res);
}

// ---------------------------------------------------------------------------
// Update preliminary discussion
// ---------------------------------------------------------------------------

export interface UpdateOppositionPreliminaryPayload {
  markName?: string;
  contactName?: string;
  phone?: string;
  email?: string;
  callTime?: string;
}

export async function updateOppositionPreliminary(
  leadId: string,
  payload: UpdateOppositionPreliminaryPayload
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/preliminary`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string }>(res);
}

// ---------------------------------------------------------------------------
// Skip step (Progress button)
// ---------------------------------------------------------------------------

export interface SkipOppositionStepResponse {
  step: string;
  status: string;
}

export async function skipOppositionStep(
  leadId: string,
  step: string
): Promise<SkipOppositionStepResponse> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/workflow/skip-step`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ step }),
    }
  );
  return parseResponse<SkipOppositionStepResponse>(res);
}

// ---------------------------------------------------------------------------
// Workflow progress
// ---------------------------------------------------------------------------

export interface OppositionProgressStep {
  slug: string;
  label: string;
  stage: string;
  stepType: string;
  parentStep?: string;
  isCompleted: boolean;
  isSkipped: boolean;
}

export interface OppositionProgressResponse {
  currentStep: string | null;
  currentSection: string | null;
  maxStepReached: string | null;
  steps: OppositionProgressStep[];
}

export async function getOppositionProgress(
  leadId: string
): Promise<OppositionProgressResponse> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/progress`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionProgressResponse>(res);
}

export interface OppositionApplicationProgressStep {
  slug: string;
  label: string;
  isCompleted: boolean;
  isSkipped: boolean;
}

export interface OppositionApplicationProgressEntry {
  conflictingMarkId: string;
  applicationNo: string | null;
  steps: OppositionApplicationProgressStep[];
}

export async function getOppositionApplicationProgress(
  leadId: string
): Promise<OppositionApplicationProgressEntry[]> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/application-progress`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionApplicationProgressEntry[]>(res);
}

// ---------------------------------------------------------------------------
// Application Details from Client (Step 2)
// ---------------------------------------------------------------------------

export interface OppositionApplicationDetailsDocument {
  id: string;
  fileKey: string;
  fileName: string;
  fileUrl: string | null;
}

export interface OppositionApplicationDetailsResponse {
  leadId: string;
  documents: OppositionApplicationDetailsDocument[];
}

export async function getOppositionApplicationDetailsFromClient(
  leadId: string
): Promise<OppositionApplicationDetailsResponse> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/application-details-from-client`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionApplicationDetailsResponse>(res);
}

export interface OppositionUploadPresignedUrl {
  key: string;
  bucket: string;
  url: string;
}

export async function generateOppositionApplicationDetailsPresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<OppositionUploadPresignedUrl[]> {
  const res = await apiFetch(`${API_BASE}/upload/presigned-urls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      files,
      type: "private",
      folder: `opposition/discussion/${leadId}`,
    }),
  });
  return parseResponse<OppositionUploadPresignedUrl[]>(res);
}

export interface SaveOppositionApplicationDetailsPayload {
  documents: Array<{ fileKey: string; fileName: string }>;
}

export async function saveOppositionApplicationDetailsFromClient(
  leadId: string,
  payload: SaveOppositionApplicationDetailsPayload
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/application-details-from-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string }>(res);
}

// ---------------------------------------------------------------------------
// Status Details (Step 3)
// ---------------------------------------------------------------------------

export interface OppositionConflictingMarkItem {
  id: string;
  nameOfProprietor: string | null;
  addressOfProprietor: string | null;
  placeOfProprietor: string | null;
  conflictingMark: string | null;
  applicationNo: string | null;
  imageFileKey: string | null;
  classOfMark: number | null;
  goodsServices: string | null;
  userDate: string | null;
  journalDate: string | null;
  lastDateOfFilingOpposition: string | null;
  statusOfConflictingMarkFileKey: string | null;
  statusOfConflictingMarkFilename: string | null;
  proceedWithOpposition: boolean | null;
}

export interface OppositionStatusDetailsData {
  leadId: string;
  mark: string | null;
  tmNumber: string | null;
  selectedClass: number | null;
  typeOfMark: string | null;
  goodsServices: string | null;
  imageOfMarkKey: string | null;
  noOfConflictingApplications: number | null;
  proceedWithOpposition: boolean | null;
  noOfApplicationsToBeOpposed: number | null;
  applicant: string | null;
  applicantAddressLine: string | null;
  applicantAddressDistrict: string | null;
  applicantAddressState: string | null;
  applicantAddressCountry: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  applicantTypeOther: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
  userDate: string | null;
  dateOfFiling: string | null;
  addresseeDesignation: string | null;
  addresseeCustomName: string | null;
  firmName: string | null;
  place: string | null;
  addresseeHonorific: string | null;
  conflictingMarks: OppositionConflictingMarkItem[];
  source: {
    referenceLeadId: string | null;
    markFromModule1: string | null;
    classesFromModule1: number[];
    applicantPrefill: {
      applicant: string | null;
      applicantAddressLine: string | null;
      applicantAddressDistrict: string | null;
      applicantAddressState: string | null;
      applicantAddressCountry: string | null;
      applicantType: string | null;
      applicantTypeOther: string | null;
      signatoryName: string | null;
      signatoryDesignation: string | null;
    } | null;
    goodsServicesFromModule1: string | null;
    imageOfMarkKeyFromModule1: string | null;
    tmNumberFromModule1: string | null;
  };
}

export interface OppositionConflictingMarkPayload {
  nameOfProprietor?: string;
  addressOfProprietor?: string;
  placeOfProprietor?: string;
  conflictingMark?: string;
  applicationNo?: string;
  imageFileKey?: string;
  classOfMark?: number;
  goodsServices?: string;
  userDate?: string;
  journalDate?: string;
  lastDateOfFilingOpposition?: string;
  statusOfConflictingMarkFileKey?: string;
  statusOfConflictingMarkFilename?: string;
}

export interface SaveOppositionStatusDetailsPayload {
  mark?: string;
  tmNumber?: string | null;
  selectedClass?: number;
  typeOfMark?: string | null;
  goodsServices?: string | null;
  imageOfMarkKey?: string | null;
  noOfConflictingApplications?: number;
  applicant?: string | null;
  applicantAddressLine?: string | null;
  applicantAddressDistrict?: string | null;
  applicantAddressState?: string | null;
  applicantAddressCountry?: string | null;
  applicantType?: string | null;
  applicantTypeOther?: string | null;
  signatoryName?: string | null;
  signatoryDesignation?: string | null;
  userDate?: string | null;
  dateOfFiling?: string | null;
  addresseeDesignation?: string | null;
  addresseeCustomName?: string | null;
  firmName?: string | null;
  place?: string | null;
  addresseeHonorific?: string | null;
  conflictingMarks: OppositionConflictingMarkPayload[];
}

export async function getOppositionStatusDetails(
  leadId: string
): Promise<OppositionStatusDetailsData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/status-details`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionStatusDetailsData>(res);
}

export async function saveOppositionStatusDetails(
  leadId: string,
  payload: SaveOppositionStatusDetailsPayload
): Promise<OppositionStatusDetailsData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/status-details`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionStatusDetailsData>(res);
}

export async function generateOppositionStatusDetailsPresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<OppositionUploadPresignedUrl[]> {
  const res = await apiFetch(`${API_BASE}/upload/presigned-urls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      files,
      type: "private",
      folder: `opposition/status-details/${leadId}`,
    }),
  });
  return parseResponse<OppositionUploadPresignedUrl[]>(res);
}

// ---------------------------------------------------------------------------
// Status Details constants
// ---------------------------------------------------------------------------

export const OPPOSITION_ADDRESSEE_DESIGNATIONS = [
  "The Proprietor",
  "The Managing Partner",
  "The Designated Partner",
  "The Managing Director",
  "The Managing Trustee",
  "The Chairman",
  "NAME",
] as const;

export const OPPOSITION_APPLICANT_TYPE_OPTIONS = [
  { value: "SOLE_PROPRIETORSHIP", label: "Sole Proprietorship" },
  { value: "TRUST", label: "Trust" },
  { value: "PARTNERSHIP_FIRM", label: "Partnership Firm" },
  { value: "SOCIETY", label: "Society" },
  { value: "PRIVATE_LIMITED_COMPANY", label: "Private Limited Company" },
  { value: "LIMITED_LIABILITY_PARTNERSHIP", label: "Limited Liability Partnership" },
  { value: "OTHERS", label: "Others" },
] as const;

export const OPPOSITION_HONORIFIC_OPTIONS = ["Sir", "Madam"] as const;

export const OPPOSITION_TYPE_OF_MARK_OPTIONS = [
  "Word",
  "Label",
  "Logo",
  "Slogan",
] as const;

export const OPPOSITION_CLASS_NUMBERS = Array.from({ length: 44 }, (_, i) => i + 1);

export const OPPOSITION_KERALA_DISTRICTS = [
  "Thiruvananthapuram",
  "Kollam",
  "Pathanamthitta",
  "Alappuzha",
  "Kottayam",
  "Idukki",
  "Ernakulam",
  "Thrissur",
  "Palakkad",
  "Malappuram",
  "Kozhikode",
  "Wayanad",
  "Kannur",
  "Kasaragod",
] as const;

// ---------------------------------------------------------------------------
// Notice to Client (Step 4)
// ---------------------------------------------------------------------------

export interface OppositionNoticeToClientPayload {
  sendWhatsApp?: boolean;
  sendEmail?: boolean;
}

export interface OppositionNoticeToClientPreviewResponse {
  sendWhatsApp: boolean;
  sendEmail: boolean;
  document: { url: string | null; filename: string };
  whatsappMessage: string | null;
  emailPreview: { to: string | null; subject: string; body: string } | null;
}

export interface OppositionNoticeToClientSendResult {
  leadId: string;
  waLink?: string;
  email?: string;
  errors?: { whatsapp?: string; email?: string };
}

export async function getOppositionNoticeToClientPreview(
  leadId: string,
  payload: OppositionNoticeToClientPayload = {}
): Promise<OppositionNoticeToClientPreviewResponse> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/notice-to-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionNoticeToClientPreviewResponse>(res);
}

export async function sendOppositionNoticeToClient(
  leadId: string,
  payload: OppositionNoticeToClientPayload = {}
): Promise<OppositionNoticeToClientSendResult> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/notice-to-client/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionNoticeToClientSendResult>(res);
}

// ---------------------------------------------------------------------------
// Inform Client (Step 5)
// ---------------------------------------------------------------------------

export interface OppositionInformClientPreviewResponse {
  message: string | null;
  recipientPhone: string | null;
}

export interface OppositionInformClientSendResult {
  leadId: string;
  waLink?: string | null;
  sentViaApi?: boolean;
  message?: string;
}

export async function getOppositionInformClientPreview(
  leadId: string
): Promise<OppositionInformClientPreviewResponse> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/inform-client/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionInformClientPreviewResponse>(res);
}

export async function sendOppositionInformClient(
  leadId: string
): Promise<OppositionInformClientSendResult> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/inform-client/whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    }
  );
  return parseResponse<OppositionInformClientSendResult>(res);
}

// ---------------------------------------------------------------------------
// Call Client for Opposition (Step 6)
// ---------------------------------------------------------------------------

export async function recordCallClientForOpposition(
  leadId: string
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/call-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    }
  );
  return parseResponse<{ leadId: string }>(res);
}

// ---------------------------------------------------------------------------
// Client Confirmation for Opposition (Step 7)
// ---------------------------------------------------------------------------

export interface SaveOppositionClientConfirmationPayload {
  confirmations: Array<{ conflictingMarkId: string; proceed: boolean }>;
}

export interface OppositionClientConfirmationResult {
  leadId: string;
  proceedWithOpposition: boolean;
  noOfApplicationsToBeOpposed: number;
  confirmations: Array<{ conflictingMarkId: string; proceed: boolean }>;
}

export async function saveOppositionClientConfirmation(
  leadId: string,
  payload: SaveOppositionClientConfirmationPayload
): Promise<OppositionClientConfirmationResult> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/client-confirmation`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionClientConfirmationResult>(res);
}

// ---------------------------------------------------------------------------
// Invoice to Client (Step 8)
// ---------------------------------------------------------------------------

export interface OppositionInvoiceApplication {
  conflictingMarkId: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  classOfMark: number | null;
  nameOfProprietor: string | null;
  placeOfProprietor: string | null;
  lastDateOfFilingOpposition: string | null;
}

export interface OppositionInvoicePreviewData {
  leadId: string;
  markName: string | null;
  selectedClass: number | null;
  noOfApplicationsToBeOpposed: number;
  applications: OppositionInvoiceApplication[];
  govtFeePerApp: number;
  govtFeeTotal: number;
  profFeePerApp: number;
  profFeeTotal: number;
  discountPerApp: number;
  totalDiscount: number;
  totalChargesBeforeDeductions: number;
  invoiceAmount: number;
  invoiceNo: string | null;
  includeInvoiceNo: boolean;
  invoiceDate: string | null;
  addressee: {
    designation: string | null;
    customName: string | null;
    firmName: string | null;
    place: string | null;
  };
  formOfAddress: string | null;
  invoiceDocumentUrl: string | null;
  invoiceDocumentFilename: string | null;
}

export interface SaveOppositionInvoicePayload {
  invoiceDate?: string;
  includeInvoiceNo?: boolean;
  govtFeePerApp?: number;
  profFeePerApp?: number;
  discountPerApp?: number;
  invoiceAmount?: number;
}

export interface OppositionGeneratedInvoiceDocument {
  s3Key: string;
  downloadUrl: string;
  filename: string;
}

export interface OppositionInvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
}

export interface OppositionSendInvoiceToClientPreview {
  sendWhatsApp: boolean;
  sendEmail: boolean;
  invoiceDocument: { key: string | null; url: string | null; filename: string };
  whatsappMessage: string | null;
  emailPreview: OppositionInvoiceEmailPreview | null;
  paymentMessagePreview: string | null;
}

export async function getOppositionInvoicePreview(
  leadId: string
): Promise<OppositionInvoicePreviewData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/invoice/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionInvoicePreviewData>(res);
}

export async function saveOppositionInvoice(
  leadId: string,
  payload: SaveOppositionInvoicePayload
): Promise<OppositionInvoicePreviewData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/invoice/save`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionInvoicePreviewData>(res);
}

export async function generateOppositionInvoicePdf(
  leadId: string
): Promise<OppositionGeneratedInvoiceDocument> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/invoice/generate`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionGeneratedInvoiceDocument>(res);
}

export async function previewOppositionSendInvoiceToClient(
  leadId: string,
  payload: { sendWhatsApp?: boolean; sendEmail?: boolean } = {}
): Promise<OppositionSendInvoiceToClientPreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/invoice/send-to-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionSendInvoiceToClientPreview>(res);
}

export async function sendOppositionInvoiceToClient(
  leadId: string,
  payload: { sendWhatsApp?: boolean; sendEmail?: boolean } = {}
): Promise<{ leadId: string; waLink?: string; email?: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/invoice/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; waLink?: string; email?: string }>(res);
}

export async function sendOppositionInvoicePaymentMessage(
  leadId: string
): Promise<{ leadId: string; waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/invoice/send-payment-message`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ leadId: string; waLink: string }>(res);
}

// ---------------------------------------------------------------------------
// Payment from Client (Step 9)
// ---------------------------------------------------------------------------

export interface OppositionPaymentContext {
  invoiceAmount: number | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  markName: string | null;
}

export async function getOppositionPaymentPreview(
  leadId: string
): Promise<OppositionPaymentContext> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/payment/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionPaymentContext>(res);
}

export async function saveOppositionPayment(
  leadId: string,
  payload: { collectedAmount: number }
): Promise<OppositionPaymentContext> {
  const res = await apiFetch(
    `${API_BASE}/opposition/discussion/${leadId}/payment/save`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionPaymentContext>(res);
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Filing Stage 1: Prepare Opposition
// ---------------------------------------------------------------------------

export interface OppositionApplicantSnapshot {
  mark: string | null;
  selectedClass: number | null;
  gsTypeA: string | null;
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  applicantTypeOther: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
  goodsServices: string | null;
  source: { referenceLeadId: string | null; markFromModule1: string | null };
}

export interface ConflictingMarkPrepareItem {
  id: string;
  nameOfProprietor: string | null;
  placeOfProprietor: string | null;
  conflictingMark: string | null;
  applicationNo: string | null;
  classOfMark: number | null;
  goodsServices: string | null;
  userDate: string | null;
  journalDate: string | null;
  lastDateOfFilingOpposition: string | null;
  applicationDate: string | null;
  journalNo: string | null;
  proceedWithOpposition: boolean | null;
}

export interface OppositionPrepareMarkVariation {
  id: string;
  mark: string;
  classNumber: number | null;
  applicationNo: string | null;
}

export interface OppositionPrepareObjectionSection {
  id: string;
  sectionHeading: string;
  sectionText: string | null;
}

export interface OppositionPrepareData {
  leadId: string;
  applicant: OppositionApplicantSnapshot;
  noOfConflictingApplications: number | null;
  noOfApplicationsToBeOpposed: number | null;
  conflictingMarks: ConflictingMarkPrepareItem[];
  operatingOutsideIndia: boolean | null;
  operatingOutsideIndiaText: string;
  dateOfOpposition: string | null;
  dateOfOppositionText: string | null;
  markVariations: OppositionPrepareMarkVariation[];
  objectionSections: OppositionPrepareObjectionSection[];
}

export async function getOppositionPrepare(leadId: string): Promise<OppositionPrepareData> {
  const res = await apiFetch(`${API_BASE}/opposition/filing/${leadId}/prepare`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  return parseResponse<OppositionPrepareData>(res);
}

export interface SaveOppositionPrepareMarkVariation {
  mark: string;
  classNumber?: number;
  applicationNo?: string;
}

export interface SaveOppositionPrepareObjectionSection {
  sectionHeading: string;
  sectionText?: string;
}

export interface SaveOppositionPrepareConflictingMark {
  conflictingMarkId: string;
  applicationDate?: string | null;
  journalNo?: string | null;
}

export interface SaveOppositionPreparePayload {
  mark?: string;
  operatingOutsideIndia?: boolean | null;
  dateOfOpposition?: string | null;
  conflictingMarks?: SaveOppositionPrepareConflictingMark[];
  markVariations: SaveOppositionPrepareMarkVariation[];
  objectionSections: SaveOppositionPrepareObjectionSection[];
}

export async function saveOppositionPrepare(
  leadId: string,
  payload: SaveOppositionPreparePayload,
): Promise<OppositionPrepareData> {
  const res = await apiFetch(`${API_BASE}/opposition/filing/${leadId}/prepare`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return parseResponse<OppositionPrepareData>(res);
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Filing Stage 2 Step 2: File Opposition
// ---------------------------------------------------------------------------

export interface FileOppositionItem {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  dateOfOpposition: string | null;
  oppositionNo: string | null;
  oppositionPdfFileKey: string | null;
  oppositionPdfFilename: string | null;
  oppositionPdfDownloadUrl: string | null;
}

export interface FileOppositionData {
  leadId: string;
  noOfApplicationsToBeOpposed: number | null;
  conflictingMarks: FileOppositionItem[];
}

export interface SaveFileOppositionItemPayload {
  conflictingMarkId: string;
  dateOfOpposition?: string | null;
  oppositionNo?: string | null;
  oppositionPdfFileKey?: string | null;
  oppositionPdfFilename?: string | null;
}

export interface SaveFileOppositionPayload {
  conflictingMarks: SaveFileOppositionItemPayload[];
}

export async function getOppositionFileOpposition(
  leadId: string
): Promise<FileOppositionData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/filing/${leadId}/file-opposition`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<FileOppositionData>(res);
}

export async function saveOppositionFileOpposition(
  leadId: string,
  payload: SaveFileOppositionPayload
): Promise<FileOppositionData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/filing/${leadId}/file-opposition`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<FileOppositionData>(res);
}

export async function generateOppositionFileOppositionPresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<OppositionUploadPresignedUrl[]> {
  const res = await apiFetch(`${API_BASE}/upload/presigned-urls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      files,
      type: "private",
      folder: `opposition/filing/${leadId}`,
    }),
  });
  return parseResponse<OppositionUploadPresignedUrl[]>(res);
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Filing Stage Step 3: Inform Client of Filing
// ---------------------------------------------------------------------------

export interface OppositionInformClientOfFilingPreviewResponse {
  message: string;
  recipientPhone: string;
  conflictingMarkName: string;
  applicationNumbers: string;
}

export interface OppositionInformClientOfFilingSendResult {
  leadId: string;
  waLink: string;
  message: string;
  sentViaApi: boolean;
}

export async function getOppositionInformClientOfFilingPreview(
  leadId: string
): Promise<OppositionInformClientOfFilingPreviewResponse> {
  const res = await apiFetch(
    `${API_BASE}/opposition/filing/${leadId}/inform-client-of-filing/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionInformClientOfFilingPreviewResponse>(res);
}

export async function sendOppositionInformClientOfFilingWhatsApp(
  leadId: string
): Promise<OppositionInformClientOfFilingSendResult> {
  const res = await apiFetch(
    `${API_BASE}/opposition/filing/${leadId}/inform-client-of-filing/whatsapp`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionInformClientOfFilingSendResult>(res);
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Filing Stage Step 4: Notice of Opposition by Registry
// ---------------------------------------------------------------------------

export type CounterDeadlineStatus = 'NOT_APPLICABLE' | 'PENDING' | 'DUE_TODAY' | 'OVERDUE';

export interface NoticeOfOppositionItemResponse {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  dateOfNoticeOfOpposition: string | null;
  lastDateForCounter: string | null;
  counterDeadlineStatus: CounterDeadlineStatus;
  daysToCounterDeadline: number | null;
}

export interface NoticeOfOppositionData {
  leadId: string;
  noOfApplicationsToBeOpposed: number | null;
  conflictingMarks: NoticeOfOppositionItemResponse[];
}

export interface SaveNoticeOfOppositionItemPayload {
  conflictingMarkId: string;
  dateOfNoticeOfOpposition?: string | null;
  lastDateForCounter?: string | null;
}

export interface SaveNoticeOfOppositionPayload {
  conflictingMarks: SaveNoticeOfOppositionItemPayload[];
}

export async function getOppositionNoticeOfOpposition(
  leadId: string
): Promise<NoticeOfOppositionData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/filing/${leadId}/notice-of-opposition`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<NoticeOfOppositionData>(res);
}

export async function saveOppositionNoticeOfOpposition(
  leadId: string,
  payload: SaveNoticeOfOppositionPayload
): Promise<NoticeOfOppositionData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/filing/${leadId}/notice-of-opposition`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<NoticeOfOppositionData>(res);
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Filing Stage Step 5: Inform Client about Notice of Opposition by Registry
// ---------------------------------------------------------------------------

export interface OppositionInformClientOfNoticeOfOppositionPreviewResponse {
  message: string;
  recipientPhone: string;
  conflictingMarkName: string;
  applicationNumbers: string;
  lastDateForCounter: string;
}

export interface OppositionInformClientOfNoticeOfOppositionSendResult {
  leadId: string;
  waLink: string;
  message: string;
  sentViaApi: boolean;
}

export async function getOppositionInformClientOfNoticeOfOppositionPreview(
  leadId: string
): Promise<OppositionInformClientOfNoticeOfOppositionPreviewResponse> {
  const res = await apiFetch(
    `${API_BASE}/opposition/filing/${leadId}/inform-client-of-notice-of-opposition/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionInformClientOfNoticeOfOppositionPreviewResponse>(res);
}

export async function sendOppositionInformClientOfNoticeOfOppositionWhatsApp(
  leadId: string
): Promise<OppositionInformClientOfNoticeOfOppositionSendResult> {
  const res = await apiFetch(
    `${API_BASE}/opposition/filing/${leadId}/inform-client-of-notice-of-opposition/whatsapp`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionInformClientOfNoticeOfOppositionSendResult>(res);
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 1: Counter by Conflicting Proprietor
// ---------------------------------------------------------------------------

export interface CounterByOpponentItem {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  dateOfNoticeOfOpposition: string | null;
  lastDateForCounter: string | null;
  counterFiled: boolean | null;
  dateOfCounter: string | null;
  dateOfReceiptOfCounter: string | null;
  lastDateOfFilingEvidence: string | null;
  counterStatementFileKey: string | null;
  counterStatementFilename: string | null;
  counterStatementDownloadUrl: string | null;
  imageOfMarkUrl: string | null;
}

export interface CounterByOpponentData {
  leadId: string;
  noOfApplicationsToBeOpposed: number | null;
  conflictingMarks: CounterByOpponentItem[];
}

export interface SaveCounterByOpponentItemPayload {
  conflictingMarkId: string;
  counterFiled?: boolean | null;
  dateOfCounter?: string | null;
  dateOfReceiptOfCounter?: string | null;
  lastDateOfFilingEvidence?: string | null;
  counterStatementFileKey?: string | null;
  counterStatementFilename?: string | null;
}

export interface SaveCounterByOpponentPayload {
  conflictingMarks: SaveCounterByOpponentItemPayload[];
}

export async function getOppositionCounterByOpponent(
  leadId: string
): Promise<CounterByOpponentData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/counter-by-opponent`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<CounterByOpponentData>(res);
}

export async function saveOppositionCounterByOpponent(
  leadId: string,
  payload: SaveCounterByOpponentPayload
): Promise<CounterByOpponentData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/counter-by-opponent`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<CounterByOpponentData>(res);
}

export async function generateOppositionCounterStatementPresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<OppositionUploadPresignedUrl[]> {
  const res = await apiFetch(`${API_BASE}/upload/presigned-urls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      files,
      type: "private",
      folder: `opposition/counter-statement/${leadId}`,
    }),
  });
  return parseResponse<OppositionUploadPresignedUrl[]>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 2: Notice to Client for Counter by Conflicting Proprietor
// ---------------------------------------------------------------------------

export interface SendOppositionNoticeToClientForCounterPayload {
  conflictingMarkIds: string[];
  sendWhatsApp?: boolean;
  sendEmail?: boolean;
}

export interface OppositionNoticeToClientForCounterResult {
  leadId: string;
  waLink?: string;
  email?: string;
  filename: string;
  conflictingMarkIds: string[];
  errors?: { whatsapp?: string; email?: string };
}

export async function sendOppositionNoticeToClientForCounter(
  leadId: string,
  payload: SendOppositionNoticeToClientForCounterPayload
): Promise<OppositionNoticeToClientForCounterResult> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/notice-to-client-for-counter`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionNoticeToClientForCounterResult>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 3: Inform Client about Counter by Conflicting Proprietor
// ---------------------------------------------------------------------------

export interface OppositionInformClientAboutCounterPreviewPayload {
  conflictingMarkIds: string[];
}

export interface OppositionInformClientAboutCounterPreview {
  message: string;
  recipientPhone: string;
  counterFiled: boolean;
  conflictingMarkIds: string[];
  conflictingMarkName: string;
  applicationNumbers: string;
  lastDateOfFilingEvidence: string | null;
  usageFromDate: string | null;
}

export interface OppositionInformClientAboutCounterSendResult {
  leadId: string;
  waLink: string;
  message: string;
  sentViaApi: boolean;
  conflictingMarkIds: string[];
}

export async function previewOppositionInformClientAboutCounter(
  leadId: string,
  payload: OppositionInformClientAboutCounterPreviewPayload
): Promise<OppositionInformClientAboutCounterPreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/inform-client-about-counter/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionInformClientAboutCounterPreview>(res);
}

export async function sendOppositionInformClientAboutCounter(
  leadId: string,
  payload: OppositionInformClientAboutCounterPreviewPayload
): Promise<OppositionInformClientAboutCounterSendResult> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/inform-client-about-counter`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionInformClientAboutCounterSendResult>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 4: Call Client for Evidence by Opponent
// ---------------------------------------------------------------------------

export interface RecordOppositionCallClientForEvidencePayload {
  conflictingMarkIds: string[];
}

export interface OppositionCallClientForEvidenceResult {
  leadId: string;
  conflictingMarkIds: string[];
}

export async function recordOppositionCallClientForEvidence(
  leadId: string,
  payload: RecordOppositionCallClientForEvidencePayload
): Promise<OppositionCallClientForEvidenceResult> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/call-client-for-evidence`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionCallClientForEvidenceResult>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 5: Confirmation from Client for Evidence by Opponent
// ---------------------------------------------------------------------------

export interface OppositionEvidenceConfirmationItem {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  proceedWithEvidence: boolean | null;
}

export interface OppositionEvidenceConfirmationData {
  leadId: string;
  noOfApplicationsToBeOpposed: number | null;
  noOfApplicationsForEvidence: number | null;
  conflictingMarks: OppositionEvidenceConfirmationItem[];
}

export interface SaveOppositionEvidenceConfirmationItemPayload {
  conflictingMarkId: string;
  proceedWithEvidence: boolean;
}

export interface SaveOppositionEvidenceConfirmationPayload {
  confirmations: SaveOppositionEvidenceConfirmationItemPayload[];
  noOfApplicationsForEvidence?: number | null;
}

export async function getOppositionEvidenceConfirmation(
  leadId: string
): Promise<OppositionEvidenceConfirmationData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-confirmation`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionEvidenceConfirmationData>(res);
}

export async function saveOppositionEvidenceConfirmation(
  leadId: string,
  payload: SaveOppositionEvidenceConfirmationPayload
): Promise<OppositionEvidenceConfirmationData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-confirmation`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionEvidenceConfirmationData>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 6: Invoice to Client for Evidence by Opponent
// ---------------------------------------------------------------------------

export type OppositionEvidenceInvoiceStatus = "ACTIVE" | "INACTIVE" | "CANCELLED";

export interface OppositionEvidenceInvoiceLine {
  conflictingMarkId: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  classOfMark: number | null;
  nameOfProprietor: string | null;
  placeOfProprietor: string | null;
  lastDateOfFilingEvidence: string | null;
  profFeePerApp: number;
  discountPerApp: number;
  lineAmount: number;
  invoiceDate: string | null;
}

export interface OppositionEvidenceInvoicePreview {
  invoiceId: string | null;
  status: OppositionEvidenceInvoiceStatus | null;
  leadId: string;
  markName: string | null;
  selectedClass: number | null;
  noOfApplicationsForEvidence: number;
  applications: OppositionEvidenceInvoiceLine[];
  profFeeTotal: number;
  totalDiscount: number;
  totalChargesBeforeDeductions: number;
  invoiceAmount: number;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  invoiceNo: string | null;
  includeInvoiceNo: boolean;
  addressee: {
    designation: string | null;
    customName: string | null;
    firmName: string | null;
    place: string | null;
  };
  formOfAddress: string | null;
  invoiceDocumentUrl: string | null;
  invoiceDocumentFilename: string | null;
  sentAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  replacedByInvoiceId: string | null;
}

export interface OppositionEvidenceInvoiceEligibleApp {
  conflictingMarkId: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  classOfMark: number | null;
  nameOfProprietor: string | null;
  placeOfProprietor: string | null;
  lastDateOfFilingEvidence: string | null;
  activeInvoice: { invoiceId: string; invoiceNo: string | null } | null;
}

export interface OppositionEvidenceInvoiceEligibleData {
  leadId: string;
  markName: string | null;
  selectedClass: number | null;
  defaultProfFeePerApp: number;
  defaultInvoiceAmountIfAllAvailable: number;
  totalEligible: number;
  totalAlreadyBilled: number;
  applications: OppositionEvidenceInvoiceEligibleApp[];
}

export interface SaveOppositionEvidenceInvoiceLinePayload {
  conflictingMarkId: string;
  profFeePerApp?: number;
  discountPerApp?: number;
  invoiceDate?: string;
  lineAmount?: number;
}

export interface SaveOppositionEvidenceInvoicePayload {
  invoiceId?: string;
  lines: SaveOppositionEvidenceInvoiceLinePayload[];
  includeInvoiceNo?: boolean;
}

export interface OppositionEvidenceInvoiceSendPreview {
  sendWhatsApp: boolean;
  sendEmail: boolean;
  invoiceDocument: { key: string | null; url: string | null; filename: string };
  whatsappMessage: string | null;
  emailPreview: { to: string | null; subject: string; body: string } | null;
  paymentMessagePreview: string | null;
}

export async function listOppositionEvidenceInvoices(
  leadId: string
): Promise<OppositionEvidenceInvoicePreview[]> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-invoice/list`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionEvidenceInvoicePreview[]>(res);
}

export async function getOppositionEvidenceInvoiceEligible(
  leadId: string
): Promise<OppositionEvidenceInvoiceEligibleData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-invoice/eligible-applications`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionEvidenceInvoiceEligibleData>(res);
}

export async function previewOppositionEvidenceInvoice(
  leadId: string,
  payload: { invoiceId?: string; conflictingMarkIds?: string[] }
): Promise<OppositionEvidenceInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-invoice/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionEvidenceInvoicePreview>(res);
}

export async function saveOppositionEvidenceInvoice(
  leadId: string,
  payload: SaveOppositionEvidenceInvoicePayload
): Promise<OppositionEvidenceInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-invoice/save`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionEvidenceInvoicePreview>(res);
}

export async function generateOppositionEvidenceInvoicePdf(
  leadId: string,
  invoiceId: string
): Promise<OppositionGeneratedInvoiceDocument> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-invoice/${invoiceId}/generate`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionGeneratedInvoiceDocument>(res);
}

export async function previewSendOppositionEvidenceInvoice(
  leadId: string,
  invoiceId: string,
  payload: { sendWhatsApp?: boolean; sendEmail?: boolean } = {}
): Promise<OppositionEvidenceInvoiceSendPreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-invoice/${invoiceId}/send-to-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionEvidenceInvoiceSendPreview>(res);
}

export async function sendOppositionEvidenceInvoiceToClient(
  leadId: string,
  invoiceId: string,
  payload: { sendWhatsApp?: boolean; sendEmail?: boolean } = {}
): Promise<{ leadId: string; invoiceId: string; waLink?: string; email?: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-invoice/${invoiceId}/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; invoiceId: string; waLink?: string; email?: string }>(res);
}

export async function sendOppositionEvidenceInvoicePaymentMessage(
  leadId: string,
  invoiceId: string
): Promise<{ leadId: string; invoiceId: string; waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-invoice/${invoiceId}/send-payment-message`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ leadId: string; invoiceId: string; waLink: string }>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 7: Payment from Client for Evidence by Opponent
// ---------------------------------------------------------------------------

export async function getOppositionEvidencePaymentStatus(
  leadId: string
): Promise<OppositionEvidenceInvoicePreview[]> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-payment`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionEvidenceInvoicePreview[]>(res);
}

export async function saveOppositionEvidencePayment(
  leadId: string,
  payload: { invoiceId: string; collectedAmount: number }
): Promise<OppositionEvidenceInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-payment`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionEvidenceInvoicePreview>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 8 / 9: Stamp Paper for Evidence by Opponent
// ---------------------------------------------------------------------------

export interface OppositionStampPaperForEvidenceItem {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  stampPapersForEvidence: number | null;
  stampVendorNameForEvidence: string | null;
  stampVendorPhoneForEvidence: string | null;
  defaultAmountPayableToStampVendorForEvidence: number;
  amountPayableToStampVendorForEvidence: number;
  paidToVendorForEvidence: boolean | null;
  paymentStatusDisplay: string;
}

export interface OppositionStampPaperForEvidenceData {
  leadId: string;
  isActive: boolean;
  stampPaperRate: number;
  conflictingMarks: OppositionStampPaperForEvidenceItem[];
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  userDate: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
}

export interface SaveOppositionStampPaperForEvidenceItem {
  conflictingMarkId: string;
  stampPapersForEvidence: number;
  stampVendorNameForEvidence: string;
  stampVendorPhoneForEvidence: string;
}

export async function getOppositionStampPaperForEvidence(
  leadId: string
): Promise<OppositionStampPaperForEvidenceData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/stamp-paper-for-evidence`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionStampPaperForEvidenceData>(res);
}

export async function saveOppositionStampPaperForEvidence(
  leadId: string,
  payload: { applications: SaveOppositionStampPaperForEvidenceItem[] }
): Promise<OppositionStampPaperForEvidenceData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/stamp-paper-for-evidence`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionStampPaperForEvidenceData>(res);
}

export async function previewOppositionStampPaperToVendor(
  leadId: string,
  conflictingMarkIds?: string[]
): Promise<{ active: boolean; message: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/stamp-paper-for-evidence/send-to-vendor/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(conflictingMarkIds ? { conflictingMarkIds } : {}),
    }
  );
  return parseResponse<{ active: boolean; message: string }>(res);
}

export async function sendOppositionStampPaperToVendor(
  leadId: string,
  conflictingMarkIds?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/stamp-paper-for-evidence/send-to-vendor`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(conflictingMarkIds ? { conflictingMarkIds } : {}),
    }
  );
  return parseResponse<{ waLink: string }>(res);
}

export interface SaveOppositionStampPaperPaymentItem {
  conflictingMarkId: string;
  amountPayableToStampVendorForEvidence?: number | null;
  paidToVendorForEvidence: boolean;
}

export async function saveOppositionStampPaperPaymentToVendor(
  leadId: string,
  payload: { items: SaveOppositionStampPaperPaymentItem[] }
): Promise<OppositionStampPaperForEvidenceData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/stamp-paper-payment-to-vendor`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionStampPaperForEvidenceData>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 10: Documents from Client for Evidence by Opponent
// ---------------------------------------------------------------------------

export interface OppositionDocumentsForEvidenceItem {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  invoiceNo: string | null;
  noOfExhibitsForEvidence: number | null;
}

export interface OppositionDocumentsForEvidenceData {
  leadId: string;
  isActive: boolean;
  noOfExhibitsForEvidence: number | null;
  conflictingMarks: OppositionDocumentsForEvidenceItem[];
}

export async function getOppositionDocumentsFromClientForEvidence(
  leadId: string
): Promise<OppositionDocumentsForEvidenceData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/documents-from-client-for-evidence`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionDocumentsForEvidenceData>(res);
}

export async function saveOppositionDocumentsFromClientForEvidence(
  leadId: string,
  payload: {
    applications: Array<{ conflictingMarkId: string; noOfExhibitsForEvidence: number }>;
  }
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/documents-from-client-for-evidence`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  await parseResponse<undefined>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 11: Preparation of Evidence Affidavit by Opponent
// ---------------------------------------------------------------------------

export type EaoTemplateVariant =
  | "SOLE_PROPRIETORSHIP"
  | "FIRMS_NGO_COMPANY"
  | "OTHERS";

export interface OppositionEvidenceAffidavitItem {
  evidenceAffidavitNo: number;
  conflictingMarkId: string;
  applicationNo: string | null;
  mark: string | null;
  classNumber: number | null;
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  signatoryDetails: string | null;
  userDate: string | null;
  descriptionOfApplicant: string | null;
  gsTypeA: string;
  gsTypeB: string;
  gsTypeC: string;
  nameOfConflictingProprietor: string | null;
  addressOfConflictingProprietor: string | null;
  conflictingMark: string | null;
  conflictingMarkImageUrl: string | null;
  classOfConflictingMark: number | null;
  goodsServicesOfConflictingMark: string | null;
  userDateOfConflictingMark: string | null;
  oppositionNo: string | null;
  dateOfNoticeOfOpposition: string | null;
  dateOfReceiptOfCounter: string | null;
  noOfExhibitsForEvidence: number | null;
  noOfParagraphsInConflictingIntro: number | null;
  paragraphWiseReplies: Array<{ heading: string; text: string }>;
  nameOfOpponentsAttorney: string | null;
  addressOfOpponentsAttorney: string | null;
  emailOfOpponentsAttorney: string | null;
  dateOfEvidenceAffidavit: string | null;
  dateOfEvidenceAffidavitFormatted: string | null;
  evidenceAffidavitPdfKey: string | null;
  evidenceAffidavitPdfFilename: string | null;
  evidenceAffidavitPdfUrl: string | null;
  applicantTypeVariant: EaoTemplateVariant;
}

export interface OppositionEvidenceAffidavitData {
  leadId: string;
  isActive: boolean;
  evidenceAffidavits: OppositionEvidenceAffidavitItem[];
}

export interface SaveOppositionEvidenceAffidavitApplication {
  conflictingMarkId: string;
  noOfParagraphsInConflictingIntro?: number | null;
  paragraphWiseReplies?: Array<{ heading?: string; text?: string }>;
  nameOfOpponentsAttorney?: string | null;
  addressOfOpponentsAttorney?: string | null;
  emailOfOpponentsAttorney?: string | null;
  dateOfEvidenceAffidavit?: string | null;
}

export async function getOppositionEvidenceAffidavit(
  leadId: string
): Promise<OppositionEvidenceAffidavitData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-affidavit`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionEvidenceAffidavitData>(res);
}

export async function saveOppositionEvidenceAffidavit(
  leadId: string,
  payload: { applications: SaveOppositionEvidenceAffidavitApplication[] }
): Promise<OppositionEvidenceAffidavitData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-affidavit`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionEvidenceAffidavitData>(res);
}

export async function generateOppositionEvidenceAffidavitPdf(
  leadId: string,
  conflictingMarkId: string
): Promise<OppositionGeneratedInvoiceDocument> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-affidavit/${conflictingMarkId}/generate-pdf`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionGeneratedInvoiceDocument>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 12: Signature from Client for Evidence by Opponent
// ---------------------------------------------------------------------------

export async function recordOppositionSignatureFromClientForEvidence(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ leadId: string; conflictingMarkIds: string[] }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/signature-from-client-for-evidence`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; conflictingMarkIds: string[] }>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 13: Notarisation of Documents for Evidence
// ---------------------------------------------------------------------------

export interface OppositionNotarisationForEvidenceItem {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  stampPapersForEvidence: number | null;
  amountPayableToNotaryForEvidenceAutoCalculated: number;
  amountPayableToNotaryForEvidence: number;
  paidToNotaryForEvidence: boolean | null;
  paymentStatusDisplay: string;
}

export interface OppositionNotarisationForEvidenceData {
  leadId: string;
  isActive: boolean;
  notaryRate: number;
  conflictingMarks: OppositionNotarisationForEvidenceItem[];
}

export async function getOppositionNotarisationForEvidence(
  leadId: string
): Promise<OppositionNotarisationForEvidenceData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/notarisation-for-evidence`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionNotarisationForEvidenceData>(res);
}

export async function saveOppositionNotarisationForEvidence(
  leadId: string,
  payload: {
    applications: Array<{
      conflictingMarkId: string;
      amountPayableToNotaryForEvidence?: number | null;
      paidToNotaryForEvidence: boolean;
    }>;
  }
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/notarisation-for-evidence`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  await parseResponse<undefined>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 14: Scanning of Documents for Evidence
// ---------------------------------------------------------------------------

export async function recordOppositionScanningOfDocumentsForEvidence(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ leadId: string; conflictingMarkIds: string[] }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/scanning-of-documents-for-evidence`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; conflictingMarkIds: string[] }>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 15: File Evidence Affidavit by Opponent
// ---------------------------------------------------------------------------

export interface OppositionFileEvidenceAffidavitItem {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  dateOfFilingEvidenceAffidavit: string | null;
  lastDateForEvidenceByConflictingProprietor: string | null;
  evidenceAffidavitFileKey: string | null;
  evidenceAffidavitFilename: string | null;
  evidenceAffidavitDownloadUrl: string | null;
}

export interface OppositionFileEvidenceAffidavitData {
  leadId: string;
  noOfApplicationsToBeOpposed: number | null;
  conflictingMarks: OppositionFileEvidenceAffidavitItem[];
}

export async function getOppositionFileEvidenceAffidavit(
  leadId: string
): Promise<OppositionFileEvidenceAffidavitData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-evidence-affidavit`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionFileEvidenceAffidavitData>(res);
}

export async function saveOppositionFileEvidenceAffidavit(
  leadId: string,
  payload: {
    conflictingMarks: Array<{
      conflictingMarkId: string;
      dateOfFilingEvidenceAffidavit?: string | null;
      lastDateForEvidenceByConflictingProprietor?: string | null;
      evidenceAffidavitFileKey?: string | null;
      evidenceAffidavitFilename?: string | null;
    }>;
  }
): Promise<OppositionFileEvidenceAffidavitData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-evidence-affidavit`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionFileEvidenceAffidavitData>(res);
}

export async function generateOppositionFileEvidenceAffidavitPresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<OppositionUploadPresignedUrl[]> {
  const res = await apiFetch(`${API_BASE}/upload/presigned-urls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      files,
      type: "private",
      folder: `opposition/status/${leadId}/file-evidence-affidavit`,
    }),
  });
  return parseResponse<OppositionUploadPresignedUrl[]>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 16: Inform Client about Filing of Evidence
// ---------------------------------------------------------------------------

export async function previewOppositionInformClientAboutFilingEvidence(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ message: string; recipientPhone: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-evidence-affidavit/inform-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ message: string; recipientPhone: string }>(res);
}

export async function sendOppositionInformClientAboutFilingEvidence(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ leadId: string; conflictingMarkIds: string[]; waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-evidence-affidavit/inform-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{
    leadId: string;
    conflictingMarkIds: string[];
    waLink: string;
  }>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Step 17/18/19: Evidence by Conflicting Proprietor flow
// ---------------------------------------------------------------------------

export interface OppositionEvidenceByCpItem {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  lastDateForEvidenceByConflictingProprietor: string | null;
  evidenceFiledByConflictingProprietor: boolean | null;
  dateOfEvidenceByConflictingProprietor: string | null;
  dateOfReceiptOfEvidenceFromConflictingProprietor: string | null;
  lastDateOfFilingRejoinder: string | null;
  evidenceFileKey: string | null;
  evidenceFilename: string | null;
  evidenceDownloadUrl: string | null;
}

export interface OppositionEvidenceByCpData {
  leadId: string;
  noOfApplicationsToBeOpposed: number | null;
  conflictingMarks: OppositionEvidenceByCpItem[];
}

export async function getOppositionEvidenceByConflictingProprietor(
  leadId: string
): Promise<OppositionEvidenceByCpData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-by-conflicting-proprietor`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionEvidenceByCpData>(res);
}

export async function saveOppositionEvidenceByConflictingProprietor(
  leadId: string,
  payload: {
    conflictingMarks: Array<{
      conflictingMarkId: string;
      evidenceFiledByConflictingProprietor?: boolean | null;
      dateOfEvidenceByConflictingProprietor?: string | null;
      dateOfReceiptOfEvidenceFromConflictingProprietor?: string | null;
      lastDateOfFilingRejoinder?: string | null;
      evidenceFileKey?: string | null;
      evidenceFilename?: string | null;
    }>;
  }
): Promise<OppositionEvidenceByCpData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/evidence-by-conflicting-proprietor`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionEvidenceByCpData>(res);
}

export async function generateOppositionEvidenceByCpPresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<OppositionUploadPresignedUrl[]> {
  const res = await apiFetch(`${API_BASE}/upload/presigned-urls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      files,
      type: "private",
      folder: `opposition/status/${leadId}/evidence-by-cp`,
    }),
  });
  return parseResponse<OppositionUploadPresignedUrl[]>(res);
}

export interface OppositionNoticeForEvidenceByCpPreview {
  whatsappMessage: string | null;
  emailPreview: { to: string | null; subject: string; body: string } | null;
  documents: Array<{
    conflictingMarkId: string;
    applicationNo: string | null;
    filename: string | null;
    available: boolean;
  }>;
}

export async function previewOppositionNoticeForEvidenceByCp(
  leadId: string,
  payload: { conflictingMarkIds: string[]; sendWhatsApp?: boolean; sendEmail?: boolean }
): Promise<OppositionNoticeForEvidenceByCpPreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/notice-to-client-for-evidence-by-cp/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionNoticeForEvidenceByCpPreview>(res);
}

export async function sendOppositionNoticeForEvidenceByCp(
  leadId: string,
  payload: { conflictingMarkIds: string[]; sendWhatsApp?: boolean; sendEmail?: boolean }
): Promise<{ leadId: string; waLink?: string; email?: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/notice-to-client-for-evidence-by-cp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; waLink?: string; email?: string }>(res);
}

export async function previewOppositionInformClientAboutEvidenceByCp(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ message: string; recipientPhone: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/inform-client-about-evidence-by-cp/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ message: string; recipientPhone: string }>(res);
}

export async function sendOppositionInformClientAboutEvidenceByCp(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ leadId: string; waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/inform-client-about-evidence-by-cp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; waLink: string }>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Steps 20–25: Rejoinder flow
// ---------------------------------------------------------------------------

// Step 20: Call Client for Rejoinder
export async function recordOppositionCallClientForRejoinder(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ leadId: string; conflictingMarkIds: string[] }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/call-client-for-rejoinder`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; conflictingMarkIds: string[] }>(res);
}

// Step 21: Rejoinder confirmation
export interface OppositionRejoinderConfirmationItem {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  lastDateOfFilingRejoinder: string | null;
  proceedWithRejoinder: boolean | null;
}

export interface OppositionRejoinderConfirmationData {
  leadId: string;
  noOfApplicationsForEvidence: number | null;
  noOfApplicationsForRejoinder: number | null;
  conflictingMarks: OppositionRejoinderConfirmationItem[];
}

export async function getOppositionRejoinderConfirmation(
  leadId: string
): Promise<OppositionRejoinderConfirmationData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/rejoinder-confirmation`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionRejoinderConfirmationData>(res);
}

export async function saveOppositionRejoinderConfirmation(
  leadId: string,
  payload: {
    confirmations: Array<{ conflictingMarkId: string; proceedWithRejoinder: boolean }>;
    noOfApplicationsForRejoinder?: number | null;
  }
): Promise<OppositionRejoinderConfirmationData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/rejoinder-confirmation`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionRejoinderConfirmationData>(res);
}

// Step 22: Rejoinder invoice (same shape as Evidence Invoice but per-line field is
// `lastDateOfFilingRejoinder` instead of `lastDateOfFilingEvidence`).
export interface OppositionRejoinderInvoiceLine
  extends Omit<OppositionEvidenceInvoiceLine, "lastDateOfFilingEvidence"> {
  lastDateOfFilingRejoinder: string | null;
}

export interface OppositionRejoinderInvoicePreview
  extends Omit<OppositionEvidenceInvoicePreview, "applications" | "noOfApplicationsForEvidence"> {
  noOfApplicationsForRejoinder: number;
  applications: OppositionRejoinderInvoiceLine[];
}

export interface OppositionRejoinderInvoiceEligibleApp
  extends Omit<OppositionEvidenceInvoiceEligibleApp, "lastDateOfFilingEvidence"> {
  lastDateOfFilingRejoinder: string | null;
}

export interface OppositionRejoinderInvoiceEligibleData
  extends Omit<OppositionEvidenceInvoiceEligibleData, "applications"> {
  applications: OppositionRejoinderInvoiceEligibleApp[];
}

export async function listOppositionRejoinderInvoices(
  leadId: string
): Promise<OppositionRejoinderInvoicePreview[]> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/rejoinder-invoice/list`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionRejoinderInvoicePreview[]>(res);
}

export async function getOppositionRejoinderInvoiceEligible(
  leadId: string
): Promise<OppositionRejoinderInvoiceEligibleData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/rejoinder-invoice/eligible-applications`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionRejoinderInvoiceEligibleData>(res);
}

export async function previewOppositionRejoinderInvoice(
  leadId: string,
  payload: { invoiceId?: string; conflictingMarkIds?: string[] }
): Promise<OppositionRejoinderInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/rejoinder-invoice/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionRejoinderInvoicePreview>(res);
}

export async function saveOppositionRejoinderInvoice(
  leadId: string,
  payload: SaveOppositionEvidenceInvoicePayload
): Promise<OppositionRejoinderInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/rejoinder-invoice/save`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionRejoinderInvoicePreview>(res);
}

export async function generateOppositionRejoinderInvoicePdf(
  leadId: string,
  invoiceId: string
): Promise<OppositionGeneratedInvoiceDocument> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/rejoinder-invoice/${invoiceId}/generate`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionGeneratedInvoiceDocument>(res);
}

export async function previewSendOppositionRejoinderInvoice(
  leadId: string,
  invoiceId: string,
  payload: { sendWhatsApp?: boolean; sendEmail?: boolean } = {}
): Promise<OppositionEvidenceInvoiceSendPreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/rejoinder-invoice/${invoiceId}/send-to-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionEvidenceInvoiceSendPreview>(res);
}

export async function sendOppositionRejoinderInvoiceToClient(
  leadId: string,
  invoiceId: string,
  payload: { sendWhatsApp?: boolean; sendEmail?: boolean } = {}
): Promise<{ leadId: string; invoiceId: string; waLink?: string; email?: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/rejoinder-invoice/${invoiceId}/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; invoiceId: string; waLink?: string; email?: string }>(res);
}

export async function sendOppositionRejoinderInvoicePaymentMessage(
  leadId: string,
  invoiceId: string
): Promise<{ leadId: string; invoiceId: string; waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/rejoinder-invoice/${invoiceId}/send-payment-message`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ leadId: string; invoiceId: string; waLink: string }>(res);
}

// Step 23: Rejoinder payment (note: invoiceId is in the URL, not the body)
export async function getOppositionRejoinderPaymentStatus(
  leadId: string
): Promise<OppositionRejoinderInvoicePreview[]> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/rejoinder-payment`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionRejoinderInvoicePreview[]>(res);
}

export async function saveOppositionRejoinderPayment(
  leadId: string,
  invoiceId: string,
  payload: { collectedAmount: number }
): Promise<OppositionRejoinderInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/rejoinder-payment/${invoiceId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionRejoinderInvoicePreview>(res);
}

// Step 24: File Rejoinder Affidavit
export interface OppositionFileRejoinderAffidavitItem {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  dateOfFilingRejoinder: string | null;
  rejoinderAffidavitFileKey: string | null;
  rejoinderAffidavitFilename: string | null;
  rejoinderAffidavitDownloadUrl: string | null;
}

export interface OppositionFileRejoinderAffidavitData {
  leadId: string;
  noOfApplicationsForRejoinder: number | null;
  conflictingMarks: OppositionFileRejoinderAffidavitItem[];
}

export async function getOppositionFileRejoinderAffidavit(
  leadId: string
): Promise<OppositionFileRejoinderAffidavitData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-rejoinder-affidavit`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionFileRejoinderAffidavitData>(res);
}

export async function saveOppositionFileRejoinderAffidavit(
  leadId: string,
  payload: {
    conflictingMarks: Array<{
      conflictingMarkId: string;
      dateOfFilingRejoinder?: string | null;
      rejoinderAffidavitFileKey?: string | null;
      rejoinderAffidavitFilename?: string | null;
    }>;
  }
): Promise<OppositionFileRejoinderAffidavitData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-rejoinder-affidavit`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionFileRejoinderAffidavitData>(res);
}

export async function generateOppositionFileRejoinderAffidavitPresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<OppositionUploadPresignedUrl[]> {
  const res = await apiFetch(`${API_BASE}/upload/presigned-urls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      files,
      type: "private",
      folder: `opposition/status/${leadId}/file-rejoinder-affidavit`,
    }),
  });
  return parseResponse<OppositionUploadPresignedUrl[]>(res);
}

// Step 25: Inform Client about Filing of Rejoinder
export async function previewOppositionInformClientAboutFilingRejoinder(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ message: string; recipientPhone: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-rejoinder-affidavit/inform-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ message: string; recipientPhone: string }>(res);
}

export async function sendOppositionInformClientAboutFilingRejoinder(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ leadId: string; conflictingMarkIds: string[]; waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-rejoinder-affidavit/inform-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{
    leadId: string;
    conflictingMarkIds: string[];
    waLink: string;
  }>(res);
}

// ---------------------------------------------------------------------------
// Stage 3 (Status) — Steps 26–47: Opposition Hearing flow
// ---------------------------------------------------------------------------

// Step 26: Status details of Opposition Hearing
export interface OppositionHearingItem {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  dateOfOppositionHearing: string | null;
  hearingNoticeFileKey: string | null;
  hearingNoticeFilename: string | null;
  hearingNoticeDownloadUrl: string | null;
}

export interface OppositionHearingData {
  leadId: string;
  noOfApplicationsForHearing: number | null;
  conflictingMarks: OppositionHearingItem[];
}

export async function getOppositionHearing(
  leadId: string
): Promise<OppositionHearingData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionHearingData>(res);
}

export async function saveOppositionHearing(
  leadId: string,
  payload: {
    conflictingMarks: Array<{
      conflictingMarkId: string;
      dateOfOppositionHearing?: string | null;
      hearingNoticeFileKey?: string | null;
      hearingNoticeFilename?: string | null;
    }>;
  }
): Promise<OppositionHearingData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionHearingData>(res);
}

export async function generateOppositionHearingNoticePresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<OppositionUploadPresignedUrl[]> {
  const res = await apiFetch(`${API_BASE}/upload/presigned-urls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      files,
      type: "private",
      folder: `opposition/status/${leadId}/hearing-notice`,
    }),
  });
  return parseResponse<OppositionUploadPresignedUrl[]>(res);
}

// Step 27: Notice to Client for Hearing
export interface OppositionNoticeToClientForHearingPreviewResponse {
  whatsappMessage: string;
  emailPreview: { subject: string; body: string };
  filename: string;
  recipientPhone: string;
  recipientEmail: string;
  conflictingMarkIds: string[];
}

export async function previewOppositionNoticeToClientForHearing(
  leadId: string,
  payload: { conflictingMarkIds: string[]; sendWhatsApp?: boolean; sendEmail?: boolean }
): Promise<OppositionNoticeToClientForHearingPreviewResponse> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing/notice-to-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionNoticeToClientForHearingPreviewResponse>(res);
}

export async function sendOppositionNoticeToClientForHearing(
  leadId: string,
  payload: { conflictingMarkIds: string[]; sendWhatsApp?: boolean; sendEmail?: boolean }
): Promise<{ leadId: string; waLink?: string; email?: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing/send-notice-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; waLink?: string; email?: string }>(res);
}

// Step 28: Inform Client about Opposition Hearing
export async function previewOppositionInformClientAboutHearing(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ message: string; recipientPhone: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing/inform-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ message: string; recipientPhone: string }>(res);
}

export async function sendOppositionInformClientAboutHearing(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ leadId: string; waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing/inform-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; waLink: string }>(res);
}

// Step 29: Invoice for Opposition Hearing (same shape as Evidence Invoice)
export interface OppositionHearingInvoiceLine
  extends Omit<OppositionEvidenceInvoiceLine, "lastDateOfFilingEvidence"> {
  dateOfOppositionHearing: string | null;
  dateOfFiling: string | null;
}

export interface OppositionHearingInvoicePreview
  extends Omit<OppositionEvidenceInvoicePreview, "applications" | "noOfApplicationsForEvidence"> {
  noOfApplicationsForHearing: number;
  applications: OppositionHearingInvoiceLine[];
}

export interface OppositionHearingInvoiceEligibleApp
  extends Omit<OppositionEvidenceInvoiceEligibleApp, "lastDateOfFilingEvidence"> {
  dateOfOppositionHearing: string | null;
  dateOfFiling: string | null;
}

export interface OppositionHearingInvoiceEligibleData
  extends Omit<OppositionEvidenceInvoiceEligibleData, "applications"> {
  applications: OppositionHearingInvoiceEligibleApp[];
}

export async function listOppositionHearingInvoices(
  leadId: string
): Promise<OppositionHearingInvoicePreview[]> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing-invoice/list`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionHearingInvoicePreview[]>(res);
}

export async function getOppositionHearingInvoiceEligible(
  leadId: string
): Promise<OppositionHearingInvoiceEligibleData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing-invoice/eligible-applications`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionHearingInvoiceEligibleData>(res);
}

export async function previewOppositionHearingInvoice(
  leadId: string,
  payload: { invoiceId?: string; conflictingMarkIds?: string[] }
): Promise<OppositionHearingInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing-invoice/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionHearingInvoicePreview>(res);
}

export async function saveOppositionHearingInvoice(
  leadId: string,
  payload: SaveOppositionEvidenceInvoicePayload
): Promise<OppositionHearingInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing-invoice/save`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionHearingInvoicePreview>(res);
}

export async function generateOppositionHearingInvoicePdf(
  leadId: string,
  invoiceId: string
): Promise<OppositionGeneratedInvoiceDocument> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing-invoice/${invoiceId}/generate`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionGeneratedInvoiceDocument>(res);
}

export async function previewSendOppositionHearingInvoice(
  leadId: string,
  invoiceId: string,
  payload: { sendWhatsApp?: boolean; sendEmail?: boolean } = {}
): Promise<OppositionEvidenceInvoiceSendPreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing-invoice/${invoiceId}/send-to-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionEvidenceInvoiceSendPreview>(res);
}

export async function sendOppositionHearingInvoiceToClient(
  leadId: string,
  invoiceId: string,
  payload: { sendWhatsApp?: boolean; sendEmail?: boolean } = {}
): Promise<{ leadId: string; invoiceId: string; waLink?: string; email?: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing-invoice/${invoiceId}/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; invoiceId: string; waLink?: string; email?: string }>(res);
}

export async function sendOppositionHearingInvoicePaymentMessage(
  leadId: string,
  invoiceId: string
): Promise<{ leadId: string; invoiceId: string; waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing-invoice/${invoiceId}/send-payment-message`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ leadId: string; invoiceId: string; waLink: string }>(res);
}

// Step 30: Call Client for Opposition Hearing
export async function recordOppositionCallClientForHearing(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ leadId: string; conflictingMarkIds: string[] }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/call-client-for-hearing`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; conflictingMarkIds: string[] }>(res);
}

// Step 31: Confirmation from Client for Opposition Hearing
export type OppositionHearingConfirmationDecision = "YES" | "NO" | "POSTPONE";

export interface OppositionHearingConfirmationItem {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  dateOfOppositionHearing: string | null;
  confirmation: OppositionHearingConfirmationDecision | null;
}

export interface OppositionHearingConfirmationData {
  leadId: string;
  noOfApplicationsForHearing: number | null;
  conflictingMarks: OppositionHearingConfirmationItem[];
}

export async function getOppositionHearingConfirmation(
  leadId: string
): Promise<OppositionHearingConfirmationData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing-confirmation`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionHearingConfirmationData>(res);
}

export async function saveOppositionHearingConfirmation(
  leadId: string,
  payload: {
    confirmations: Array<{
      conflictingMarkId: string;
      confirmation: OppositionHearingConfirmationDecision;
    }>;
    noOfApplicationsForHearing?: number | null;
  }
): Promise<OppositionHearingConfirmationData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing-confirmation`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionHearingConfirmationData>(res);
}

// Step 32: TM-M Invoice before Opposition Hearing (uses dateOfOppositionHearing
// per line instead of lastDateOfFilingEvidence).
export interface OppositionTmMInvoiceLine
  extends Omit<OppositionEvidenceInvoiceLine, "lastDateOfFilingEvidence"> {
  dateOfOppositionHearing: string | null;
}

export interface OppositionTmMInvoicePreview
  extends Omit<OppositionEvidenceInvoicePreview, "applications"> {
  applications: OppositionTmMInvoiceLine[];
}

export interface OppositionTmMInvoiceEligibleApp
  extends Omit<OppositionEvidenceInvoiceEligibleApp, "lastDateOfFilingEvidence"> {
  dateOfOppositionHearing: string | null;
}

export interface OppositionTmMInvoiceEligibleData
  extends Omit<OppositionEvidenceInvoiceEligibleData, "applications"> {
  applications: OppositionTmMInvoiceEligibleApp[];
}

export async function listOppositionTmMInvoices(
  leadId: string
): Promise<OppositionTmMInvoicePreview[]> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tm-m-invoice/list`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionTmMInvoicePreview[]>(res);
}

export async function getOppositionTmMInvoiceEligible(
  leadId: string
): Promise<OppositionTmMInvoiceEligibleData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tm-m-invoice/eligible-applications`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionTmMInvoiceEligibleData>(res);
}

export async function previewOppositionTmMInvoice(
  leadId: string,
  payload: { invoiceId?: string; conflictingMarkIds?: string[] }
): Promise<OppositionTmMInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tm-m-invoice/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionTmMInvoicePreview>(res);
}

export async function saveOppositionTmMInvoice(
  leadId: string,
  payload: SaveOppositionEvidenceInvoicePayload
): Promise<OppositionTmMInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tm-m-invoice/save`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionTmMInvoicePreview>(res);
}

export async function generateOppositionTmMInvoicePdf(
  leadId: string,
  invoiceId: string
): Promise<OppositionGeneratedInvoiceDocument> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tm-m-invoice/${invoiceId}/generate`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionGeneratedInvoiceDocument>(res);
}

export async function previewSendOppositionTmMInvoice(
  leadId: string,
  invoiceId: string,
  payload: { sendWhatsApp?: boolean; sendEmail?: boolean } = {}
): Promise<OppositionEvidenceInvoiceSendPreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tm-m-invoice/${invoiceId}/send-to-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionEvidenceInvoiceSendPreview>(res);
}

export async function sendOppositionTmMInvoiceToClient(
  leadId: string,
  invoiceId: string,
  payload: { sendWhatsApp?: boolean; sendEmail?: boolean } = {}
): Promise<{ leadId: string; invoiceId: string; waLink?: string; email?: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tm-m-invoice/${invoiceId}/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; invoiceId: string; waLink?: string; email?: string }>(res);
}

export async function sendOppositionTmMInvoicePaymentMessage(
  leadId: string,
  invoiceId: string
): Promise<{ leadId: string; invoiceId: string; waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tm-m-invoice/${invoiceId}/send-payment-message`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ leadId: string; invoiceId: string; waLink: string }>(res);
}

// Step 33: TM-M payment
export async function getOppositionTmMInvoicePaymentStatus(
  leadId: string
): Promise<OppositionTmMInvoicePreview[]> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tm-m-invoice-payment`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionTmMInvoicePreview[]>(res);
}

export async function saveOppositionTmMInvoicePayment(
  leadId: string,
  invoiceId: string,
  payload: { collectedAmount: number }
): Promise<OppositionTmMInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tm-m-invoice-payment/${invoiceId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionTmMInvoicePreview>(res);
}

// Step 34: File TM-M before Opposition Hearing
export interface OppositionFileTmMBeforeHearingItem {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  dateOfFilingTmM: string | null;
  tmMFileKey: string | null;
  tmMFilename: string | null;
  tmMDownloadUrl: string | null;
}

export interface OppositionFileTmMBeforeHearingData {
  leadId: string;
  invoiceAmount: number | null;
  conflictingMarks: OppositionFileTmMBeforeHearingItem[];
}

export async function getOppositionFileTmMBeforeHearing(
  leadId: string
): Promise<OppositionFileTmMBeforeHearingData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-tm-m-before-hearing`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionFileTmMBeforeHearingData>(res);
}

export async function saveOppositionFileTmMBeforeHearing(
  leadId: string,
  payload: {
    conflictingMarks: Array<{
      conflictingMarkId: string;
      dateOfFilingTmM?: string | null;
      tmMFileKey?: string | null;
      tmMFilename?: string | null;
    }>;
  }
): Promise<OppositionFileTmMBeforeHearingData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-tm-m-before-hearing`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionFileTmMBeforeHearingData>(res);
}

export async function generateOppositionFileTmMBeforeHearingPresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<OppositionUploadPresignedUrl[]> {
  const res = await apiFetch(`${API_BASE}/upload/presigned-urls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      files,
      type: "private",
      folder: `opposition/status/${leadId}/file-tm-m-before-hearing`,
    }),
  });
  return parseResponse<OppositionUploadPresignedUrl[]>(res);
}

// Step 35: Inform Client about Filing of TM-M before Hearing
export async function previewOppositionInformClientAboutFilingTmMBefore(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ message: string; recipientPhone: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-tm-m-before-hearing/inform-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ message: string; recipientPhone: string }>(res);
}

export async function sendOppositionInformClientAboutFilingTmMBefore(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ leadId: string; conflictingMarkIds: string[]; waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-tm-m-before-hearing/inform-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; conflictingMarkIds: string[]; waLink: string }>(res);
}

// Step 36: Payment for Opposition Hearing (uses hearing-invoice-payment URL)
export async function getOppositionHearingInvoicePaymentStatus(
  leadId: string
): Promise<OppositionHearingInvoicePreview[]> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing-invoice-payment`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionHearingInvoicePreview[]>(res);
}

export async function saveOppositionHearingInvoicePayment(
  leadId: string,
  invoiceId: string,
  payload: { collectedAmount: number }
): Promise<OppositionHearingInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/hearing-invoice-payment/${invoiceId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionHearingInvoicePreview>(res);
}

// Step 37: Documents from Client for Hearing (pure workflow record)
export async function recordOppositionDocumentsFromClientForHearing(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ leadId: string; conflictingMarkIds: string[] }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/documents-from-client-for-hearing`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; conflictingMarkIds: string[] }>(res);
}

// Step 38: Filing of Opposition Hearing Document (pure workflow record)
export async function recordOppositionFilingOfHearingDocument(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ leadId: string; conflictingMarkIds: string[] }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/filing-of-hearing-document`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; conflictingMarkIds: string[] }>(res);
}

// Step 39: Note on Hearing
export interface OppositionNoteOnHearingItem {
  noteNo: number;
  conflictingMarkId: string;
  applicationNo: string | null;
  mark: string | null;
  selectedClass: number | null;
  typeOfMark: string | null;
  goodsServices: string | null;
  imageOfMarkUrl: string | null;
  userDate: string | null;
  applicant: string | null;
  dateOfFiling: string | null;
  dateOfOppositionHearing: string | null;
  nameOfConflictingProprietor: string | null;
  addressOfConflictingProprietor: string | null;
  placeOfConflictingProprietor: string | null;
  conflictingMark: string | null;
  conflictingMarkTmNo: string | null;
  conflictingMarkImageUrl: string | null;
  classOfConflictingMark: number | null;
  goodsServicesOfConflictingMark: string | null;
  userDateOfConflictingMark: string | null;
  oppositionNo: string | null;
  dateOfOpposition: string | null;
  similarityNote1: string | null;
  similarityNote2: string | null;
  otherOppositionNote1: string | null;
  otherOppositionNote2: string | null;
  notePdfFileKey: string | null;
  notePdfFilename: string | null;
  notePdfUrl: string | null;
}

export interface OppositionNoteOnHearingData {
  leadId: string;
  isActive: boolean;
  notes: OppositionNoteOnHearingItem[];
}

export async function getOppositionNoteOnHearing(
  leadId: string
): Promise<OppositionNoteOnHearingData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/note-on-hearing`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionNoteOnHearingData>(res);
}

export async function saveOppositionNoteOnHearing(
  leadId: string,
  conflictingMarkId: string,
  payload: {
    similarityNote1?: string | null;
    similarityNote2?: string | null;
    otherOppositionNote1?: string | null;
    otherOppositionNote2?: string | null;
  }
): Promise<OppositionNoteOnHearingItem> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/note-on-hearing/${conflictingMarkId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionNoteOnHearingItem>(res);
}

export async function generateOppositionNoteOnHearingPdf(
  leadId: string,
  conflictingMarkId: string
): Promise<OppositionGeneratedInvoiceDocument> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/note-on-hearing/${conflictingMarkId}/generate-pdf`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionGeneratedInvoiceDocument>(res);
}

// Step 40: Attend Opposition Hearing
export type OppositionAttendHearingStatus = "ATTENDED" | "NOT_ATTENDED" | "DIDNT_HAPPEN";
export type OppositionHearingOfficerRemark =
  | "ACCEPTED"
  | "REFUSED"
  | "WILL_PASS_ON_MERITS"
  | "FILE_TM_M"
  | "DIRECTION_OF_OFFICER";

export interface OppositionAttendHearingItem {
  itemNo: number;
  conflictingMarkId: string;
  applicationNo: string | null;
  conflictingMark: string | null;
  mark: string | null;
  dateOfOppositionHearing: string | null;
  hearingStatus: OppositionAttendHearingStatus | null;
  remarkOfHearingOfficer: OppositionHearingOfficerRemark | null;
  directionOfOfficer: string | null;
  requiresSubsequentHearing: boolean;
  requiresTmMFiling: boolean;
  requiresInformAfterHearing: boolean;
}

export interface OppositionAttendHearingData {
  leadId: string;
  isActive: boolean;
  items: OppositionAttendHearingItem[];
  anyRequiresTmMFiling: boolean;
  anyRequiresSubsequentHearing: boolean;
  anyRequiresInformAfterHearing: boolean;
}

export async function getOppositionAttendHearing(
  leadId: string
): Promise<OppositionAttendHearingData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/attend-hearing`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionAttendHearingData>(res);
}

export async function saveOppositionAttendHearing(
  leadId: string,
  conflictingMarkId: string,
  payload: {
    hearingStatus: OppositionAttendHearingStatus;
    remarkOfHearingOfficer?: OppositionHearingOfficerRemark | null;
    directionOfOfficer?: string | null;
  }
): Promise<OppositionAttendHearingItem> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/attend-hearing/${conflictingMarkId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionAttendHearingItem>(res);
}

// Step 41: Inform Client after Opposition Hearing
export async function previewOppositionInformClientAfterHearing(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ message: string; recipientPhone: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/attend-hearing/inform-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ message: string; recipientPhone: string }>(res);
}

export async function sendOppositionInformClientAfterHearing(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ leadId: string; waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/attend-hearing/inform-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; waLink: string }>(res);
}

// Step 42: TM-M after Hearing Invoice (same shape as TM-M before)
export type OppositionTmMAfterInvoicePreview = OppositionTmMInvoicePreview;
export type OppositionTmMAfterInvoiceEligibleData = OppositionTmMInvoiceEligibleData;

export async function listOppositionTmMAfterInvoices(
  leadId: string
): Promise<OppositionTmMAfterInvoicePreview[]> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tmm-after-hearing-invoice/list`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionTmMAfterInvoicePreview[]>(res);
}

export async function getOppositionTmMAfterInvoiceEligible(
  leadId: string
): Promise<OppositionTmMAfterInvoiceEligibleData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tmm-after-hearing-invoice/eligible-applications`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionTmMAfterInvoiceEligibleData>(res);
}

export async function previewOppositionTmMAfterInvoice(
  leadId: string,
  payload: { invoiceId?: string; conflictingMarkIds?: string[] }
): Promise<OppositionTmMAfterInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tmm-after-hearing-invoice/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionTmMAfterInvoicePreview>(res);
}

export async function saveOppositionTmMAfterInvoice(
  leadId: string,
  payload: SaveOppositionEvidenceInvoicePayload
): Promise<OppositionTmMAfterInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tmm-after-hearing-invoice/save`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionTmMAfterInvoicePreview>(res);
}

export async function generateOppositionTmMAfterInvoicePdf(
  leadId: string,
  invoiceId: string
): Promise<OppositionGeneratedInvoiceDocument> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tmm-after-hearing-invoice/${invoiceId}/generate`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionGeneratedInvoiceDocument>(res);
}

export async function previewSendOppositionTmMAfterInvoice(
  leadId: string,
  invoiceId: string,
  payload: { sendWhatsApp?: boolean; sendEmail?: boolean } = {}
): Promise<OppositionEvidenceInvoiceSendPreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tmm-after-hearing-invoice/${invoiceId}/send-to-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionEvidenceInvoiceSendPreview>(res);
}

export async function sendOppositionTmMAfterInvoiceToClient(
  leadId: string,
  invoiceId: string,
  payload: { sendWhatsApp?: boolean; sendEmail?: boolean } = {}
): Promise<{ leadId: string; invoiceId: string; waLink?: string; email?: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tmm-after-hearing-invoice/${invoiceId}/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; invoiceId: string; waLink?: string; email?: string }>(res);
}

export async function sendOppositionTmMAfterInvoicePaymentMessage(
  leadId: string,
  invoiceId: string
): Promise<{ leadId: string; invoiceId: string; waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tmm-after-hearing-invoice/${invoiceId}/send-payment-message`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ leadId: string; invoiceId: string; waLink: string }>(res);
}

// Step 43: TM-M after Hearing payment
export async function getOppositionTmMAfterInvoicePaymentStatus(
  leadId: string
): Promise<OppositionTmMAfterInvoicePreview[]> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tmm-after-hearing-invoice-payment`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionTmMAfterInvoicePreview[]>(res);
}

export async function saveOppositionTmMAfterInvoicePayment(
  leadId: string,
  invoiceId: string,
  payload: { collectedAmount: number }
): Promise<OppositionTmMAfterInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/tmm-after-hearing-invoice-payment/${invoiceId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionTmMAfterInvoicePreview>(res);
}

// Step 44: File TM-M after Hearing
export interface OppositionFileTmMAfterHearingItem {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  dateOfFilingTmM: string | null;
  tmMFileKey: string | null;
  tmMFilename: string | null;
  tmMDownloadUrl: string | null;
}

export interface OppositionFileTmMAfterHearingData {
  leadId: string;
  invoiceAmount: number | null;
  conflictingMarks: OppositionFileTmMAfterHearingItem[];
}

export async function getOppositionFileTmMAfterHearing(
  leadId: string
): Promise<OppositionFileTmMAfterHearingData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-tm-m-after-hearing`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionFileTmMAfterHearingData>(res);
}

export async function saveOppositionFileTmMAfterHearing(
  leadId: string,
  payload: {
    conflictingMarks: Array<{
      conflictingMarkId: string;
      dateOfFilingTmM?: string | null;
      tmMFileKey?: string | null;
      tmMFilename?: string | null;
    }>;
  }
): Promise<OppositionFileTmMAfterHearingData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-tm-m-after-hearing`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionFileTmMAfterHearingData>(res);
}

export async function generateOppositionFileTmMAfterHearingPresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<OppositionUploadPresignedUrl[]> {
  const res = await apiFetch(`${API_BASE}/upload/presigned-urls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      files,
      type: "private",
      folder: `opposition/status/${leadId}/file-tm-m-after-hearing`,
    }),
  });
  return parseResponse<OppositionUploadPresignedUrl[]>(res);
}

// Step 45: Inform Client after Filing TM-M after Hearing
export async function previewOppositionInformClientAboutFilingTmMAfter(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ message: string; recipientPhone: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-tm-m-after-hearing/inform-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ message: string; recipientPhone: string }>(res);
}

export async function sendOppositionInformClientAboutFilingTmMAfter(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ leadId: string; conflictingMarkIds: string[]; waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/file-tm-m-after-hearing/inform-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; conflictingMarkIds: string[]; waLink: string }>(res);
}

// ===========================================================================
// Step 46: Subsequent Opposition Hearings
// Backend: /opposition/status/:id/subsequent-hearing
//
// Step 46 is an "open next hearing round" action — it bumps the round counter
// per conflicting application. Each application is independently eligible
// when at least one of:
//   a) 21zzy) TM-M filed before Opposition Hearing (Step 34) has a date;
//   b) Hearing Status (Step 40) at the latest round is DIDNT_HAPPEN or
//      NOT_ATTENDED;
//   c) Hearing Status at the latest round is ATTENDED with no final-outcome
//      remark (FILE_TM_M / DIRECTION_OF_OFFICER or no remark yet).
// Otherwise the workflow goes directly to Step 61 (Order of Opposition
// Hearing to Client).
//
// After opening a new round, the substeps 40–59 (Hearing Status, Invoice,
// etc.) re-apply against the new round number via the existing endpoints.
// ===========================================================================

export type OppositionSubsequentHearingStatusValue =
  | "ATTENDED"
  | "NOT_ATTENDED"
  | "DIDNT_HAPPEN";

/** One round of Opposition Hearing recorded for a conflicting application. */
export interface OppositionSubsequentHearingRoundEntry {
  roundNumber: number;
  dateOfOppositionHearing: string | null;
  hearingNoticeFileKey: string | null;
  hearingNoticeFilename: string | null;
  hearingNoticeDownloadUrl: string | null;
  hearingStatus: OppositionSubsequentHearingStatusValue | null;
  remarkOfHearingOfficer: string | null;
}

export interface OppositionSubsequentHearingItem {
  conflictingMarkId: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  /** Highest round_number recorded so far (0 if Step 26 has not been saved). */
  currentRound: number;
  canOpenNextRound: boolean;
  /** Human-readable reason eligibility was granted (or denied). */
  eligibilityReason: string;
  rounds: OppositionSubsequentHearingRoundEntry[];
}

export interface OppositionSubsequentHearingData {
  leadId: string;
  items: OppositionSubsequentHearingItem[];
}

export async function getOppositionSubsequentHearing(
  leadId: string
): Promise<OppositionSubsequentHearingData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/subsequent-hearing`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionSubsequentHearingData>(res);
}

export interface OpenOppositionSubsequentHearingItem {
  conflictingMarkId: string;
  /** ISO date for the new round (optional — can be saved later via Step 26). */
  dateOfOppositionHearing?: string | null;
  /** S3 key for the Hearing Notice PDF for the new round (optional). */
  hearingNoticeFileKey?: string | null;
  hearingNoticeFilename?: string | null;
}

export async function openOppositionSubsequentHearingRound(
  leadId: string,
  payload: { conflictingMarks: OpenOppositionSubsequentHearingItem[] }
): Promise<OppositionSubsequentHearingData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/subsequent-hearing/open`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionSubsequentHearingData>(res);
}

// Step 47: Final Order to Client

// 21zzzj) Outcome of Opposition Hearing — mirrors backend FinalOrderOutcome enum.
export type OppositionFinalOrderOutcome =
  | "REFUSED_AFTER_OPPOSITION_HEARING"
  | "ACCEPTED_AFTER_OPPOSITION_HEARING"
  | "ACCEPTED_AFTER_NON_APPEARANCE_IN_HEARING"
  | "ACCEPTED_AFTER_NON_FILING_OF_EVIDENCE_BY_OPPONENT";

export interface OppositionFinalOrderItem {
  conflictingMarkId: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  finalOrderFileKey: string | null;
  finalOrderFilename: string | null;
  finalOrderDownloadUrl: string | null;
  outcomeOfHearing: OppositionFinalOrderOutcome | null;
}

export interface OppositionFinalOrderData {
  leadId: string;
  items: OppositionFinalOrderItem[];
}

export async function getOppositionFinalOrder(
  leadId: string
): Promise<OppositionFinalOrderData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/final-order`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<OppositionFinalOrderData>(res);
}

export async function saveOppositionFinalOrder(
  leadId: string,
  payload: {
    applications: Array<{
      conflictingMarkId: string;
      finalOrderFileKey?: string | null;
      finalOrderFilename?: string | null;
      outcomeOfHearing?: OppositionFinalOrderOutcome | null;
    }>;
  }
): Promise<OppositionFinalOrderData> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/final-order`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionFinalOrderData>(res);
}

export async function generateOppositionFinalOrderPresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<OppositionUploadPresignedUrl[]> {
  const res = await apiFetch(`${API_BASE}/upload/presigned-urls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      files,
      type: "private",
      folder: `opposition/status/${leadId}/final-order`,
    }),
  });
  return parseResponse<OppositionUploadPresignedUrl[]>(res);
}

export async function forwardOppositionFinalOrderPdf(
  leadId: string,
  payload: { conflictingMarkIds: string[]; sendWhatsApp?: boolean; sendEmail?: boolean }
): Promise<{ leadId: string; waLink?: string; email?: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/final-order/forward-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; waLink?: string; email?: string }>(res);
}

export interface OppositionFinalOrderMessagePreview {
  message: string;
  recipientPhone: string;
  outcomeOfHearing: OppositionFinalOrderOutcome;
  applicationNumbers: string;
  conflictingMarkIds: string[];
}

export async function previewOppositionFinalOrderOutcomeMessage(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<OppositionFinalOrderMessagePreview> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/final-order/preview-outcome-message`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<OppositionFinalOrderMessagePreview>(res);
}

export async function sendOppositionFinalOrderMessage(
  leadId: string,
  payload: { conflictingMarkIds: string[] }
): Promise<{ leadId: string; waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/opposition/status/${leadId}/final-order/send-message`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string; waLink: string }>(res);
}

/** Backend OppositionStep enum slugs used for workflow progress lookup. */
export const OppositionWorkflowStep = {
  OP_S1_01_PreliminaryDiscussion: "OP_S1_01_PRELIMINARY_DISCUSSION",
  OP_S1_02_ApplicationDetailsFromClient: "OP_S1_02_APPLICATION_DETAILS_FROM_CLIENT",
  OP_S1_03_StatusDetails: "OP_S1_03_STATUS_DETAILS",
  OP_S1_04_NoticeToClient: "OP_S1_04_NOTICE_TO_CLIENT",
  OP_S1_05_InformClient: "OP_S1_05_INFORM_CLIENT",
  OP_S1_06_CallClientForOpposition: "OP_S1_06_CALL_CLIENT_FOR_OPPOSITION",
  OP_S1_07_ClientConfirmation: "OP_S1_07_CLIENT_CONFIRMATION",
  OP_S1_08_InvoiceToClient: "OP_S1_08_INVOICE_TO_CLIENT",
  OP_S1_08A_InvoiceSaved: "OP_S1_08A_INVOICE_SAVED",
  OP_S1_08B_InvoiceGenerated: "OP_S1_08B_INVOICE_GENERATED",
  OP_S1_08C_InvoiceSentToClient: "OP_S1_08C_INVOICE_SENT_TO_CLIENT",
  OP_S1_08D_InvoicePaymentMessageSent: "OP_S1_08D_INVOICE_PAYMENT_MESSAGE_SENT",
  OP_S1_09_PaymentFromClient: "OP_S1_09_PAYMENT_FROM_CLIENT",
  OP_S2_01_PrepareOpposition: "OP_S2_01_PREPARE_OPPOSITION",
  OP_S2_02_FileOpposition: "OP_S2_02_FILE_OPPOSITION",
  OP_S2_03_InformClientOfFiling: "OP_S2_03_INFORM_CLIENT_OF_FILING",
  OP_S2_04_NoticeOfOppositionByRegistry: "OP_S2_04_NOTICE_OF_OPPOSITION_BY_REGISTRY",
  OP_S2_05_InformClientOfNoticeOfOpposition: "OP_S2_05_INFORM_CLIENT_OF_NOTICE_OF_OPPOSITION",
  OP_S3_01_CounterByConflictingProprietor: "OP_S3_01_COUNTER_BY_CONFLICTING_PROPRIETOR",
  OP_S3_02_NoticeToClientForCounter: "OP_S3_02_NOTICE_TO_CLIENT_FOR_COUNTER",
  OP_S3_03_InformClientAboutCounter: "OP_S3_03_INFORM_CLIENT_ABOUT_COUNTER",
  OP_S3_04_CallClientForEvidenceByOpponent: "OP_S3_04_CALL_CLIENT_FOR_EVIDENCE_BY_OPPONENT",
  OP_S3_05_ConfirmationFromClientForEvidenceByOpponent:
    "OP_S3_05_CONFIRMATION_FROM_CLIENT_FOR_EVIDENCE_BY_OPPONENT",
  OP_S3_06_InvoiceToClientForEvidenceByOpponent:
    "OP_S3_06_INVOICE_TO_CLIENT_FOR_EVIDENCE_BY_OPPONENT",
  OP_S3_06a_EvidenceInvoiceSaved: "OP_S3_06A_EVIDENCE_INVOICE_SAVED",
  OP_S3_06b_EvidenceInvoiceSentToClient: "OP_S3_06B_EVIDENCE_INVOICE_SENT_TO_CLIENT",
  OP_S3_06c_EvidenceInvoicePaymentMessageSent:
    "OP_S3_06C_EVIDENCE_INVOICE_PAYMENT_MESSAGE_SENT",
  OP_S3_07_PaymentFromClientForEvidenceByOpponent:
    "OP_S3_07_PAYMENT_FROM_CLIENT_FOR_EVIDENCE_BY_OPPONENT",
  OP_S3_08_StampPaperDetailsToVendorForEvidenceByOpponent:
    "OP_S3_08_STAMP_PAPER_DETAILS_TO_VENDOR_FOR_EVIDENCE_BY_OPPONENT",
  OP_S3_08a_StampPaperDetailsSentToVendor:
    "OP_S3_08A_STAMP_PAPER_DETAILS_SENT_TO_VENDOR",
  OP_S3_09_StampPaperFromVendorForEvidenceByOpponent:
    "OP_S3_09_STAMP_PAPER_FROM_VENDOR_FOR_EVIDENCE_BY_OPPONENT",
  OP_S3_10_DocumentsFromClientForEvidenceByOpponent:
    "OP_S3_10_DOCUMENTS_FROM_CLIENT_FOR_EVIDENCE_BY_OPPONENT",
  OP_S3_11_PreparationOfEvidenceAffidavitByOpponent:
    "OP_S3_11_PREPARATION_OF_EVIDENCE_AFFIDAVIT_BY_OPPONENT",
  OP_S3_12_SignatureFromClientForEvidenceByOpponent:
    "OP_S3_12_SIGNATURE_FROM_CLIENT_FOR_EVIDENCE_BY_OPPONENT",
  OP_S3_13_NotarisationForEvidenceByOpponent:
    "OP_S3_13_NOTARISATION_FOR_EVIDENCE_BY_OPPONENT",
  OP_S3_14_ScanningOfDocumentsForEvidenceByOpponent:
    "OP_S3_14_SCANNING_OF_DOCUMENTS_FOR_EVIDENCE_BY_OPPONENT",
  OP_S3_15_FileEvidenceAffidavitByOpponent:
    "OP_S3_15_FILE_EVIDENCE_AFFIDAVIT_BY_OPPONENT",
  OP_S3_16_InformClientAboutFilingOfEvidenceByOpponent:
    "OP_S3_16_INFORM_CLIENT_ABOUT_FILING_OF_EVIDENCE_BY_OPPONENT",
  OP_S3_17_EvidenceByConflictingProprietor:
    "OP_S3_17_EVIDENCE_BY_CONFLICTING_PROPRIETOR",
  OP_S3_18_NoticeToClientForEvidenceByConflictingProprietor:
    "OP_S3_18_NOTICE_TO_CLIENT_FOR_EVIDENCE_BY_CONFLICTING_PROPRIETOR",
  OP_S3_19_InformClientAboutEvidenceByConflictingProprietor:
    "OP_S3_19_INFORM_CLIENT_ABOUT_EVIDENCE_BY_CONFLICTING_PROPRIETOR",
  OP_S3_20_CallClientForRejoinderByOpponent:
    "OP_S3_20_CALL_CLIENT_FOR_REJOINDER_BY_OPPONENT",
  OP_S3_21_ConfirmationFromClientForRejoinderByOpponent:
    "OP_S3_21_CONFIRMATION_FROM_CLIENT_FOR_REJOINDER_BY_OPPONENT",
  OP_S3_22_InvoiceToClientForRejoinderByOpponent:
    "OP_S3_22_INVOICE_TO_CLIENT_FOR_REJOINDER_BY_OPPONENT",
  OP_S3_22b_RejoinderInvoiceSentToClient:
    "OP_S3_22B_REJOINDER_INVOICE_SENT_TO_CLIENT",
  OP_S3_23_PaymentFromClientForRejoinderByOpponent:
    "OP_S3_23_PAYMENT_FROM_CLIENT_FOR_REJOINDER_BY_OPPONENT",
  OP_S3_24_FileRejoinderByOpponent:
    "OP_S3_24_FILE_REJOINDER_BY_OPPONENT",
  OP_S3_25_InformClientAboutFilingOfRejoinderByOpponent:
    "OP_S3_25_INFORM_CLIENT_ABOUT_FILING_OF_REJOINDER_BY_OPPONENT",
  OP_S3_26_OppositionHearing: "OP_S3_26_OPPOSITION_HEARING",
  OP_S3_27_NoticeToClientForHearing: "OP_S3_27_NOTICE_TO_CLIENT_FOR_HEARING",
  OP_S3_28_InformClientAboutOppositionHearing:
    "OP_S3_28_INFORM_CLIENT_ABOUT_OPPOSITION_HEARING",
  OP_S3_29_InvoiceToClientForOppositionHearing:
    "OP_S3_29_INVOICE_TO_CLIENT_FOR_OPPOSITION_HEARING",
  OP_S3_29b_HearingInvoiceSentToClient:
    "OP_S3_29B_HEARING_INVOICE_SENT_TO_CLIENT",
  OP_S3_30_CallClientForOppositionHearing:
    "OP_S3_30_CALL_CLIENT_FOR_OPPOSITION_HEARING",
  OP_S3_31_ConfirmationFromClientForOppositionHearing:
    "OP_S3_31_CONFIRMATION_FROM_CLIENT_FOR_OPPOSITION_HEARING",
  OP_S3_32_TmMInvoiceToClientForOppositionHearing:
    "OP_S3_32_TMM_INVOICE_TO_CLIENT_FOR_OPPOSITION_HEARING",
  OP_S3_32b_TmMInvoiceSentToClient: "OP_S3_32B_TMM_INVOICE_SENT_TO_CLIENT",
  OP_S3_33_PaymentFromClientForTmMBeforeOppositionHearing:
    "OP_S3_33_PAYMENT_FROM_CLIENT_FOR_TMM_BEFORE_OPPOSITION_HEARING",
  OP_S3_34_FileTmMBeforeOppositionHearing:
    "OP_S3_34_FILE_TMM_BEFORE_OPPOSITION_HEARING",
  OP_S3_35_InformClientAboutFilingOfTmMBeforeOppositionHearing:
    "OP_S3_35_INFORM_CLIENT_ABOUT_FILING_OF_TMM_BEFORE_OPPOSITION_HEARING",
  OP_S3_36_PaymentFromClientForOppositionHearing:
    "OP_S3_36_PAYMENT_FROM_CLIENT_FOR_OPPOSITION_HEARING",
  OP_S3_37_DocumentsFromClientForOppositionHearing:
    "OP_S3_37_DOCUMENTS_FROM_CLIENT_FOR_OPPOSITION_HEARING",
  OP_S3_38_FilingOfOppositionHearingDocument:
    "OP_S3_38_FILING_OF_OPPOSITION_HEARING_DOCUMENT",
  OP_S3_39_NoteOnHearing: "OP_S3_39_NOTE_ON_HEARING",
  OP_S3_40_AttendOppositionHearing:
    "OP_S3_40_ATTEND_OPPOSITION_HEARING",
  OP_S3_41_InformClientAfterOppositionHearing:
    "OP_S3_41_INFORM_CLIENT_AFTER_OPPOSITION_HEARING",
  OP_S3_42_TmmAfterHearingInvoiceToClient:
    "OP_S3_42_TMM_AFTER_HEARING_INVOICE_TO_CLIENT",
  OP_S3_42b_TmmAfterHearingInvoiceSentToClient:
    "OP_S3_42B_TMM_AFTER_HEARING_INVOICE_SENT_TO_CLIENT",
  OP_S3_43_PaymentFromClientForTmmAfterHearing:
    "OP_S3_43_PAYMENT_FROM_CLIENT_FOR_TMM_AFTER_HEARING",
  OP_S3_44_FileTmmAfterOppositionHearing:
    "OP_S3_44_FILE_TMM_AFTER_OPPOSITION_HEARING",
  OP_S3_45_InformClientAfterFilingTmmAfterOppositionHearing:
    "OP_S3_45_INFORM_CLIENT_AFTER_FILING_TMM_AFTER_OPPOSITION_HEARING",
  OP_S3_46_OpenSubsequentHearingRound:
    "OP_S3_46_OPEN_SUBSEQUENT_HEARING_ROUND",
  OP_S3_47_FinalOrderToClient: "OP_S3_47_FINAL_ORDER_TO_CLIENT",
} as const;
